import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { NhaContentService } from '../../shared/services/nha-content.service';
import { STATIC_PROBLEM_STATEMENTS } from '../hackathon/hackathon.component';

interface ResourceItem {
  id: string;
  title: string;
  description: string;
  fileName: string;
  downloadUrl: string;
}

@Component({
  selector: 'app-knowledge-hub',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
  ],
  templateUrl: './knowledge-hub.component.html',
  styleUrl: './knowledge-hub.component.scss',
})
export class KnowledgeHubComponent implements OnInit {
  private readonly defaultPs1 = STATIC_PROBLEM_STATEMENTS[0];
  private readonly defaultPs2 = STATIC_PROBLEM_STATEMENTS[1];

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Knowledge hub' }
    ],
    title: 'Knowledge hub',
    description: 'Browse the knowledge base and curated content. Find articles, guides, and resources to help you get the most out of NHA.'
  };

  activeTab: 'resources' | 'stories' = 'resources';

  resources: ResourceItem[] = [
    {
      id: 'ps1',
      title: `${this.defaultPs1.tag} ${this.defaultPs1.title}`,
      description: `Download the resource bundle for ${this.defaultPs1.tag}.`,
      fileName: 'ps1.zip',
      downloadUrl: '/documents/NHA/STGs_ps1.zip',
    },
    {
      id: 'ps2',
      title: `${this.defaultPs2.tag} ${this.defaultPs2.title}`,
      description: `Download the resource bundle for ${this.defaultPs2.tag}.`,
      fileName: 'ps2.zip',
      downloadUrl: '/documents/NHA/STGs_ps2.zip',
    },
  ];

  constructor(private contentService: NhaContentService) {}

  ngOnInit(): void {
    this.contentService.fetchHackathonContent().subscribe(({ problemStatements }) => {
      if (!problemStatements || problemStatements.length === 0) {
        return;
      }

      const ps1 = this.getProblemStatement(problemStatements, 1);
      const ps2 = this.getProblemStatement(problemStatements, 2);

      if (ps1?.title) {
        const ps1Tag = ps1.tag || this.defaultPs1.tag;
        this.resources[0].title = `${ps1Tag} ${ps1.title}`;
        this.resources[0].description = `Download the resource bundle for ${ps1Tag}.`;
      }

      if (ps2?.title) {
        const ps2Tag = ps2.tag || this.defaultPs2.tag;
        this.resources[1].title = `${ps2Tag} ${ps2.title}`;
        this.resources[1].description = `Download the resource bundle for ${ps2Tag}.`;
      }
    });
  }

  setActiveTab(tab: 'resources' | 'stories'): void {
    this.activeTab = tab;
  }

  get emptyStateHint(): string {
    return this.activeTab === 'resources'
      ? 'Resources will be available here shortly.'
      : 'Stories will be available here shortly.';
  }

  trackByResourceId(_: number, item: ResourceItem): string {
    return item.id;
  }

  private getProblemStatement(
    problemStatements: Array<{ id?: number; tag?: string; title?: string }>,
    targetIndex: number,
  ): { id?: number; tag?: string; title?: string } | undefined {
    return (
      problemStatements.find((item) => item.id === targetIndex) ??
      problemStatements.find((item) => item.tag === `PS-0${targetIndex}` || item.tag === `PS-${targetIndex}`) ??
      problemStatements[targetIndex - 1]
    );
  }
}
